/*
 * SoleThreads — Welcome / customer sign-in popup (sections/solethreads-welcome-popup.liquid)
 *
 * <solethreads-welcome-popup> wraps one native <dialog>. The section only renders it for signed-out
 * visitors, so a signed-in customer never gets it.
 *   - Opens [data-delay] ms after the page has fully loaded, once per browser session: it is marked
 *     as seen in sessionStorage when it opens, so closing it, navigating, or signing in never
 *     brings it back in that session. Without sessionStorage it stays closed rather than nagging.
 *   - Waits (up to MAX_RETRIES) while another overlay is open, e.g. the founder feedback popup,
 *     the cart drawer, search, or a theme (tingle) modal.
 *   - Closes on the × button, a backdrop click, or Escape; the page behind does not scroll while
 *     it is open.
 *   - Sign-in and registration are plain links to Shopify's customer account pages; nothing here
 *     handles credentials.
 *   - Theme editor: never opens on its own; it opens while its section is selected.
 */
(() => {
  const TAG = "solethreads-welcome-popup";
  if (customElements.get(TAG)) return;

  const OPEN_CLASS = "st-wlc-open";
  const SEEN_KEY = "st-welcome-popup-seen";
  const RETRY_DELAY = 2000;
  const MAX_RETRIES = 5;

  // null when sessionStorage can't be used (blocked, private mode in some browsers).
  const wasSeen = () => {
    try {
      return window.sessionStorage.getItem(SEEN_KEY) === "1";
    } catch {
      return null;
    }
  };

  const markSeen = () => {
    try {
      window.sessionStorage.setItem(SEEN_KEY, "1");
    } catch {}
  };

  const otherOverlayOpen = () =>
    Boolean(document.querySelector("dialog[open]")) ||
    document.documentElement.matches(".open-drawer, .open-search, .st-ffb-open") ||
    document.body.classList.contains("tingle-enabled");

  class SolethreadsWelcomePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("[data-wlc-dialog]");
      if (!this.dialog) return;

      this.events = new AbortController();
      const { signal } = this.events;

      this.dialog.querySelectorAll("[data-wlc-close]").forEach((button) =>
        button.addEventListener("click", () => this.dialog.close(), { signal })
      );

      // The panel fills the dialog, so the dialog itself is only the target on the backdrop.
      // Both ends of the click must be there, so a text selection dragged outside doesn't close it.
      this.dialog.addEventListener("pointerdown", (event) => {
        this.pressedBackdrop = event.target === this.dialog;
      }, { signal });
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog && this.pressedBackdrop) this.dialog.close();
        this.pressedBackdrop = false;
      }, { signal });

      this.dialog.addEventListener("close", () => this.onClose(), { signal });

      if (this.hasAttribute("data-design-mode")) {
        this.previewInEditor(signal);
        return;
      }

      if (wasSeen() !== false) return;

      const schedule = () => {
        // Fetch the image during the delay, without competing with the page's own loading.
        const image = this.dialog.querySelector(".st-wlc__image");
        if (image) image.loading = "eager";
        this.timer = setTimeout(() => this.tryOpen(0), Number(this.dataset.delay) || 0);
      };
      if (document.readyState === "complete") schedule();
      else window.addEventListener("load", schedule, { once: true, signal });
    }

    disconnectedCallback() {
      this.events?.abort();
      clearTimeout(this.timer);
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove(OPEN_CLASS);
    }

    tryOpen(attempt) {
      if (!this.isConnected || wasSeen() !== false) return;
      if (otherOverlayOpen()) {
        if (attempt < MAX_RETRIES) this.timer = setTimeout(() => this.tryOpen(attempt + 1), RETRY_DELAY);
        return;
      }
      markSeen();
      this.open();
    }

    previewInEditor(signal) {
      const isThisSection = (event) =>
        this.closest(".shopify-section")?.id === `shopify-section-${event.detail?.sectionId}`;
      document.addEventListener("shopify:section:select", (event) => {
        if (isThisSection(event)) this.open();
      }, { signal });
      document.addEventListener("shopify:section:deselect", (event) => {
        if (isThisSection(event) && this.dialog.open) this.dialog.close();
      }, { signal });
    }

    open() {
      if (this.dialog.open) return;
      this.returnFocus = document.activeElement;

      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty("--st-wlc-scrollbar", `${Math.max(scrollbar, 0)}px`);
      document.documentElement.classList.add(OPEN_CLASS);

      if (typeof this.dialog.showModal === "function") this.dialog.showModal();
      else this.dialog.setAttribute("open", "");
    }

    onClose() {
      document.documentElement.classList.remove(OPEN_CLASS);
      document.documentElement.style.removeProperty("--st-wlc-scrollbar");
      if (this.returnFocus?.isConnected && this.returnFocus !== document.body) {
        this.returnFocus.focus({ preventScroll: true });
      }
    }
  }

  customElements.define(TAG, SolethreadsWelcomePopup);
})();
